import { Timestamp } from '@bufbuild/protobuf';
import { useEffect } from 'react';
import { useCommonServiceClientContext } from './CommonServiceClientProvider';
import { getLocalStorage, setLocalStorage } from './utilFunctions';

interface ManageSessionProps {
  role: number; // Define the type of role parameter
}
const ManageSession: React.FC<ManageSessionProps> = ({ role }) => {
  const { UmsUtilityAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const checkAndUpdateTime = async () => {
    const getCurrentTime = () => new Date().getTime();
    const getLastUpdatedTime = () => {
      const lastUpdatedTime = getLocalStorage('lastUpdatedTime');
      return lastUpdatedTime ? parseInt(lastUpdatedTime) : null;
    };

    let auth = getLocalStorage('auth');
    if (auth === 'true' || auth === 'false') {
      auth = JSON.parse(auth);
    }

    const updateTimeInLocalStorage = () => {
      const currentTime = getCurrentTime();
      setLocalStorage('lastUpdatedTime', String(currentTime));
    };

    const endSession = async (time: Timestamp) => {
      // Call your API here
      const activeSessionId = getLocalStorage('activeSession');
      try {
        const response =
          await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.upsertActiveSession(
            {
              activeSessionId: (activeSessionId ? BigInt(activeSessionId) : undefined),
              endTime: time,
              lastUsageTime: time,
              role: role,
            }
          );
        if (response?.code == '200') {
          await startSession();
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
      updateTimeInLocalStorage();
    };

    const startSession = async () => {
      // Call your API here
      const userID = getLocalStorage('userId');

      try {
        const response =
          await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.upsertActiveSession(
            {
              profileId: userID,
              startTime: Timestamp.fromDate(new Date()),
              role: role,
            }
          );

        if (
          response &&
          response.data &&
          response?.data?.case === 'activeSessionData'
        ) {
          setLocalStorage(
            'activeSession',
            response?.data?.value?.activeSessionId?.toString()
          );
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
      updateTimeInLocalStorage();
    };

    if (auth) {
      const lastUpdatedTime = getLastUpdatedTime();
      if (lastUpdatedTime !== null) {
        const currentTime = getCurrentTime();
        const lastActiveSession = getLocalStorage('activeSession');
        if (currentTime - lastUpdatedTime > 3600000) {
          // 3600000 milliseconds = 1 hour
          const date = new Date(lastUpdatedTime);
          await endSession(Timestamp.fromDate(date)); // Call API to end the session
          // await startSession(); // Call API only if time difference condition is met
        } else {
          // console.log(
          //   'Difference less than or equal to one hour. Not calling API.'
          // );
        }
      } else {
        console.log('Last updated time not found');
      }
    } else {
      console.log('Auth is false. Not calling API.');
    }

    if (auth && !document.hidden) {
      updateTimeInLocalStorage(); // Update time in localStorage if authenticated and not hidden
    } else if (!auth && !document.hidden) {
      console.log(
        'Updating every minute and not authenticated, clearing localStorage'
      );
      updateTimeInLocalStorage(); // Clear localStorage if not authenticated and not hidden
    }
  };

  const manageLastUsageTime = async (time: Timestamp) => {
    // Call your API here
    const activeSessionId = getLocalStorage('activeSession');
    try {
      const response =
        await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.upsertActiveSession(
          {
            activeSessionId: (activeSessionId ? BigInt(activeSessionId) : undefined),
            lastUsageTime: time,
            role: role,
          }
        );
    } catch (error) {
      console.log('Fetch error:', error);
    }
  };

  useEffect(() => {
    const auth = getLocalStorage('auth');
    const lastUpdatedTimeForSession = getLocalStorage(
      'lastUpdatedTimeForSession'
    );
    const activeSessionId = getLocalStorage('activeSession');
    if (auth)
      // Add type declaration for 'auth'
      checkAndUpdateTime();

    // Update time every minute
    const intervalId = setInterval(() => {
      checkAndUpdateTime();
    }, 60000); // 60000 milliseconds = 1 minute
    if (auth && activeSessionId && lastUpdatedTimeForSession) {
      manageLastUsageTime(
        Timestamp.fromDate(new Date(parseInt(lastUpdatedTimeForSession)))
      );
      localStorage.removeItem('lastUpdatedTimeForSession');
    }
    const handleTabClose = async () => {
      if (auth && activeSessionId) {
        setLocalStorage(
          'lastUpdatedTimeForSession',
          String(new Date().getTime())
        );
      }
    };

    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      clearInterval(intervalId); // Clear the interval on component unmount
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, []);

  return null;
};

export default ManageSession;
